import type { PublicProfileAnyChecklistId } from './public-profile-builder';

export type PublicProfileRouteMode = 'individual' | 'company';

export function getPublicProfileRoutes(profileMode: PublicProfileRouteMode = 'individual') {
  const base = profileMode === 'company' ? '/(employer)/company' : '/(app)/profile';
  // Analytics is a workspace-level screen (the one the sidebar links to), not a
  // child of the builder stack — hence the separate base.
  const analytics = profileMode === 'company' ? '/(employer)/analytics' : '/(app)/analytics';

  return {
    index: base,
    analytics,
    accountEdit: `${base}/account-edit`,
    editProfile: `${base}/edit-profile`,
    checklist: `${base}/checklist`,
    add: `${base}/add`,
    design: `${base}/design`,
    contacts: `${base}/contacts`,
    socials: `${base}/socials`,
    settings: `${base}/settings`,
    block: (blockId: string) => `${base}/block/${encodeURIComponent(blockId)}`,
    share: `${base}/share`,
    previewShare: `${base}/preview-share`,
    openRoles: `${base}/open-roles`,
    team: `${base}/team`,
  } as const;
}

export type PublicProfileRoutes = ReturnType<typeof getPublicProfileRoutes>;

export const PUBLIC_PROFILE_ROUTES: PublicProfileRoutes = getPublicProfileRoutes('individual');

export type PublicProfileRoutePath = string;

export function getPublicProfileChecklistRoutes(
  profileMode: PublicProfileRouteMode = 'individual',
): Record<
  PublicProfileAnyChecklistId,
  PublicProfileRoutePath
> {
  const routes = getPublicProfileRoutes(profileMode);

  return {
    // Individual checklist ids.
    name_bio: routes.editProfile,
    profile_image: routes.editProfile,
    socials: routes.socials,
    important_links: routes.add,
    customize_design: routes.design,
    share_tabbio: routes.share,
    // Company checklist ids (PRD §11): each incomplete item opens the exact
    // relevant form.
    company_identity: routes.editProfile,
    logo: routes.editProfile,
    social_or_website: routes.socials,
    careers_ready: routes.openRoles,
    shared: routes.share,
  };
}

export const PUBLIC_PROFILE_CHECKLIST_ROUTES = getPublicProfileChecklistRoutes('individual');
